import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Plus,
  Mail,
  File,
  FileImage,
  FileText,
  FileSpreadsheet,
  FileVideo,
  FileAudio,
  FileArchive,
  FileCode,
  Phone,
  Flag,
  ListTodo,
  MessageSquare,
  SquarePlus,
  SquarePen,
  History,
  Boxes,
  NotepadText,
  MoveRight,
  Lock,
  LockOpen,
  ExternalLink,
} from "lucide-react";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { createPortal } from "react-dom";
import { format, formatDistanceToNow, isSameWeek, isToday, isValid } from "date-fns";
import GroupedSectionHeader from "../components/ui/GroupedSectionHeader";
import { ActivityTypes } from "../constants/action-types";
import ActivityAction from "../actions/ActivityAction";
import { fromJS } from "immutable";
import DealBadge from "../icons/DealBadge";
import ContactBadge from "../icons/ContactBadge";
import CompanyBadge from "../icons/CompanyBadge";
import { navigateTo } from "../Navigator";
import { getJirItemUrl, isGmailContext } from "../api/api";
import { downloadFileLogActivity, getItem, updateTaskField } from "../api/ProjectApi";
import { openLink } from "../api/AppLinkApi";
import { getFieldIcon } from "../constants/fieldIconMap";
import NoteCard from "./components/card/NoteCard";
import EmailCard from "./components/card/EmailCard";
import ScheduleCard from "./components/card/ScheduleCard";
import LayoutAction from "../actions/LayoutAction";

export const formatDistanceToNowActivity = (activity: any) => {
  const createdAt = activity.getIn(["event", "createdAt"]) * 1000;
  if (isValid(new Date(createdAt))) {
    return formatDistanceToNow(createdAt);
  }
  return "";
};

export const groupByTime = (activities: any) => {
  const today = [];
  const thisWeek = [];
  const months: Record<string, any> = {};
  const now = new Date();
  activities.forEach((activity) => {
    const createdAt = activity.getIn(["event", "createdAt"]);
    const dt = new Date(createdAt * 1000);
    if (isToday(dt)) {
      today.push(activity.toJS());
      return;
    }
    if (isSameWeek(dt, now, { weekStartsOn: 1 })) {
      thisWeek.push(activity.toJS());
      return;
    }
    const label = format(dt, "MMMM yyyy");
    if (!months[label]) months[label] = [];
    months[label].push(activity.toJS());
  });
  return { today, thisWeek, months };
};

export const groupSameDay = (activities: any) => {
  return activities.groupBy((activity) => {
    const id = activity.get("id");
    const itemId = activity.get("itemId");
    const action = activity.getIn(["event", "action"]);
    const type = activity.getIn(["event", "type"]);
    const createdAt = activity.getIn(["event", "createdAt"]);
    const dateStr = format(new Date(createdAt * 1000), "yyyy-MM-dd");
    if (action === "edited") {
      return `${itemId}|${dateStr}|${action}|${type}`;
    }
    return id;
  });
};

export const FileIcon = ({
  fileName = "",
  fileContentType = "",
  className = "w-4 h-4",
}: {
  fileName?: string;
  fileContentType?: string;
  className?: string;
}) => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  // IMAGE
  if (
    fileContentType.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(extension || "")
  ) {
    return <FileImage className={`text-pink-500 ${className}`} />;
  }
  // DOC / TEXT
  if (["doc", "docx", "txt", "pdf"].includes(extension || "") || fileContentType.includes("text")) {
    return <FileText className={`text-blue-500 ${className}`} />;
  }
  // EXCEL
  if (["xls", "xlsx", "csv"].includes(extension || "") || fileContentType.includes("spreadsheet")) {
    return <FileSpreadsheet className={`text-green-600 ${className}`} />;
  }
  // VIDEO
  if (
    fileContentType.startsWith("video/") ||
    ["mp4", "mov", "avi", "mkv"].includes(extension || "")
  ) {
    return <FileVideo className={`text-red-500 ${className}`} />;
  }
  // AUDIO
  if (fileContentType.startsWith("audio/") || ["mp3", "wav", "ogg"].includes(extension || "")) {
    return <FileAudio className={`text-yellow-500 ${className}`} />;
  }
  // ZIP
  if (["zip", "rar", "7z", "tar"].includes(extension || "")) {
    return <FileArchive className={`text-orange-500 ${className}`} />;
  }
  // CODE
  if (["js", "ts", "tsx", "jsx", "html", "css", "json"].includes(extension || "")) {
    return <FileCode className={`text-purple-500 ${className}`} />;
  }
  // DEFAULT
  return <File className={className} />;
};

export const VisibilityBadge = ({ visibility }: { visibility?: string }) => {
  const { t } = useTranslation();
  if (!visibility) return null;
  return visibility === "Shared" ? (
    <div className="inline-flex items-center gap-1 px-[3px] py-[1px] bg-teal-100 rounded-full">
      <LockOpen className="w-2.5 h-2.5 text-teal-800 flex-shrink-0" />
      <span className="text-[10px] font-medium text-teal-800 leading-4 px-[2.5px] py-0">
        {t("Shared")}
      </span>
    </div>
  ) : (
    <div className="inline-flex items-center gap-1 px-[3px] py-[1px] bg-red-100 rounded-full">
      <Lock className="w-2.5 h-2.5 text-red-800 flex-shrink-0" />
      <span className="text-[10px] font-medium text-red-800 leading-4 px-[2.5px] py-0">
        {t("Private")}
      </span>
    </div>
  );
};

export const ActivityVisibility = ({
  isEditMode,
  activity,
}: {
  isEditMode: boolean;
  activity: any;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const activityId = activity.get("id");
  const itemId = activity.get("itemId");
  const itemType = activity.get("itemType");
  const visibility = activity.getIn(["event", "data", "visibility"]);

  if (!visibility) return null;

  if (isEditMode) {
    return (
      <>
        {visibility === "Private" ? (
          <div
            title={t("Change to Shared")}
            className="shrink-0 cursor-pointer"
            onClick={async () => {
              await updateTaskField(activityId, "visibility", "Shared", "ACTIVITY");
              dispatch(
                LayoutAction.setNotification({
                  title: "Visibility",
                  type: "success",
                  message: t("Changed to Shared"),
                })
              );
              dispatch(
                ActivityAction.searchActivities({
                  itemId,
                  scope: "item",
                  includeDependencies:
                    itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
                })
              );
            }}
          >
            <VisibilityBadge visibility={visibility} />
          </div>
        ) : (
          <div
            title={t("Change to Private")}
            className="shrink-0 cursor-pointer"
            onClick={async () => {
              await updateTaskField(activityId, "visibility", "Private", "ACTIVITY");
              dispatch(
                LayoutAction.setNotification({
                  title: "Visibility",
                  type: "success",
                  message: t("Changed to Private"),
                })
              );
              dispatch(
                ActivityAction.searchActivities({
                  itemId,
                  scope: "item",
                  includeDependencies:
                    itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
                })
              );
            }}
          >
            <VisibilityBadge visibility={visibility} />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="shrink-0">
      <VisibilityBadge visibility={visibility} />
    </div>
  );
};

export const ActivityIcon = ({ activity }: { activity: any }) => {
  // created, edited,
  // comment, uploadFile, commented,
  // sendEmail, sendDraftEmail, sendScheduledEmail, recordSentEmail,
  // createActivity (meeting, call, deadline, task, email, logEmail, lunch, file),
  const itemType = activity.get("itemType");
  const action = activity.getIn(["event", "action"]);
  const type = activity.getIn(["event", "type"]);
  switch (action) {
    case "created":
      if (itemType === "SALE") {
        return <DealBadge style={{ width: "16px", height: "16px" }} />;
      } else if (itemType === "CONTACT") {
        return <ContactBadge style={{ width: "16px", height: "16px" }} />;
      } else if (itemType === "COMPANY") {
        return <CompanyBadge style={{ width: "16px", height: "16px" }} />;
      } else if (itemType === "PRODUCT") {
        return <Boxes className="w-4 h-4" />;
      }
      return <SquarePlus className="w-4 h-4" />;
    case "edited":
      return <SquarePen className="w-4 h-4" />;
    case "comment":
      return <MessageSquare className="w-4 h-4" />;
    case "uploadFile":
      return (
        <FileIcon
          fileName={activity.getIn(["event", "data", "fileName"])}
          fileContentType={activity.getIn(["event", "data", "fileContentType"])}
          className="w-4 h-4"
        />
      );
    case "commented":
      return <NotepadText className="w-4 h-4" />;
    case "sent":
    case "sendEmail":
    case "sendDraftEmail":
    case "sendScheduledEmail":
    case "recordSentEmail":
      return <Mail className="w-4 h-4" />;
    case "createActivity":
      switch (type) {
        case "meeting":
          return <CalendarDays className="w-4 h-4" />;
        case "call":
          return <Phone className="w-4 h-4" />;
        case "deadline":
          return <Flag className="w-4 h-4" />;
        case "task":
          return <ListTodo className="w-4 h-4" />;
        case "email":
        case "logEmail":
          return <Mail className="w-4 h-4" />;
        case "lunch":
          return <CalendarDays className="w-4 h-4" />;
        case "file":
          return (
            <FileIcon
              fileName={activity.getIn(["event", "data", "fileName"])}
              fileContentType={activity.getIn(["event", "data", "fileContentType"])}
              className="w-4 h-4"
            />
          );
        default:
          return <History className="w-4 h-4" />;
      }
    default:
      return <History className="w-4 h-4" />;
  }
};

export const ActivityActor = ({
  activity,
  hideName = false,
  classImg = "w-5 h-5",
  classText = "font-medium text-gray-900",
}: {
  activity: any;
  hideName?: boolean;
  classImg?: string;
  classText?: string;
}) => {
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));

  const person = activity.getIn(["event", "person"]);
  const actionMember =
    members.find((member) => member.get("accountId") == person) ||
    fromJS({ accountId: person, displayName: person });
  const actorName = actionMember.get("displayName") || "";
  const actorAvatar = actionMember.get("avatarUrl") || "";

  return (
    <div className="flex items-center gap-1 shrink-0">
      {actorAvatar && (
        <img src={actorAvatar} alt="" className={`${classImg} rounded-full`} title={actorName} />
      )}
      {!hideName && <span className={classText}>{actorName}</span>}
    </div>
  );
};

export const ActivityHover = ({
  title,
  content,
  classNameButton = "",
  classNamePanel = "",
}: {
  title: string;
  content: React.ReactNode;
  classNameButton?: string;
  classNamePanel?: string;
}) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState({});

  const triggerRef = useRef(null);
  const portalRef = useRef(null);
  const showTimeout = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(showTimeout.current);
    setLoading(true);
    showTimeout.current = setTimeout(() => {
      setVisible(true);
      setLoading(false);
    }, 1000);
  };

  const handleMouseLeave = () => {
    clearTimeout(showTimeout.current);
    setLoading(false);
  };

  const updatePosition = () => {
    if (!triggerRef.current || !portalRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const portalRect = portalRef.current.getBoundingClientRect();
    const showTop = triggerRect.top > portalRect.height + 12;
    const left = triggerRect.left + triggerRect.width / 2 + window.scrollX;
    const top = showTop
      ? triggerRect.top + window.scrollY - 8
      : triggerRect.bottom + window.scrollY + 8;
    setStyle({
      position: "absolute",
      zIndex: 9999,
      top,
      left,
      transform: showTop ? "translate(-50%, -100%)" : "translate(-50%, 0)",
    });
  };

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });
    if (portalRef.current) {
      resizeObserver.observe(portalRef.current);
    }
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || portalRef.current?.contains(target)) {
        return;
      }
      setVisible(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);

  useEffect(() => {
    return () => {
      clearTimeout(showTimeout.current);
    };
  }, []);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setVisible(true)}
        className={`relative font-medium text-blue-600 cursor-pointer outline-none ${classNameButton}`}
      >
        {title}
        {loading && (
          <div className="absolute left-0 bottom-0 h-[1px] w-full overflow-hidden rounded bg-gray-200">
            <div className="h-full animate-loading-line bg-blue-500" />
          </div>
        )}
      </span>
      {visible &&
        createPortal(
          <div ref={portalRef} style={style} className="app-portal-root">
            <div className={`text-sm ${classNamePanel}`}>{content}</div>
          </div>,
          document.body
        )}
    </>
  );
};

export const ActivityViewRowContent = ({
  actor,
  item,
  activities,
  currentUser,
  fields,
  simple = false,
  className = "",
  navigateToUrl,
}: {
  actor: React.ReactNode;
  item: any;
  activities: any;
  currentUser: any;
  fields: any;
  simple?: boolean;
  className?: string;
  navigateToUrl?: (appUrl: string, jiraUrl: string) => void;
}) => {
  const { t } = useTranslation();

  // If action != "edited" have only 1 activity
  const lastActivity = activities.first();
  const action = lastActivity.getIn(["event", "action"]);
  const type = lastActivity.getIn(["event", "type"]);
  const itemId = lastActivity.get("itemId");
  const itemType = lastActivity.get("itemType");
  const summary = lastActivity.getIn(["event", "data", "summary"]);

  const isShowDetailActivity =
    lastActivity.getIn(["event", "data", "visibility"]) === "Private"
      ? lastActivity.getIn(["event", "person"]) === currentUser.get("accountId")
      : true;
  const isMyActivity = lastActivity.getIn(["event", "person"]) === currentUser.get("accountId");

  const { data: itemData, isLoading: isLoadingItem } = useQuery({
    queryKey: ["item", itemId],
    queryFn: () => getItem(itemId),
    enabled: !!itemId,
    staleTime: 60 * 1000, // cache 60s
  });

  const renderItemInfo = () => {
    if (!itemData) return null;
    if (item.get("id") === itemData.id) return null;
    let icon;
    let name;
    if (itemData.type === "SALE") {
      icon = <DealBadge style={{ width: "16px", height: "16px" }} />;
      name = t("Deal");
    } else if (itemData.type === "CONTACT") {
      icon = <ContactBadge style={{ width: "16px", height: "16px" }} />;
      name = t("Contact");
    } else if (itemData.type === "COMPANY") {
      icon = <CompanyBadge style={{ width: "16px", height: "16px" }} />;
      name = t("Account");
    } else if (itemData.type === "PRODUCT") {
      icon = <Boxes className="w-4 h-4" />;
      name = t("Product");
    }
    return (
      <div className="flex items-center gap-2 min-w-0 truncate">
        <span className="text-gray-600">{t("in")}</span>
        {icon}
        <span
          className="font-medium truncate cursor-pointer hover:underline"
          onClick={() => {
            const appUrl = `/workspaces/${itemData.projectId}/items/${itemData.id}?type=${itemData.type}`;
            const jiraUrl = getJirItemUrl({
              workspaceId: itemData.projectId,
              itemId: itemData.id,
              type: itemData.type,
            });
            navigateToUrl?.(appUrl, jiraUrl);
          }}
        >
          {itemData.data?.name?.value || name}
        </span>
      </div>
    );
  };

  const renderLinkEmail = () => {
    const threadId = lastActivity.getIn(["event", "data", "threadId"]);
    if (isGmailContext() && !!threadId) {
      try {
        const email = window.inboxSdk.User.getEmailAddress();
        const person = lastActivity.getIn(["event", "person"]);
        if (email === person) {
          return (
            <div
              title={t("Go to email")}
              className="shrink-0 cursor-pointer"
              onClick={async () => {
                window.inboxSdk.Router.goto(`all/${threadId}`);
              }}
            >
              <ExternalLink className="w-3 h-3 text-gray-600" />
            </div>
          );
        }
      } catch {}
    }
    return null;
  };

  const getActionText = () => {
    let actionText;
    switch (action) {
      case "comment":
        actionText = t("commented");
        break;
      case "uploadFile":
        actionText = t("uploaded a file");
        break;
      case "commented":
        actionText = t("created a note");
        break;
      case "sent":
        actionText = lastActivity.getIn(["event", "comment"]);
        break;
      case "sendEmail":
      case "sendDraftEmail":
      case "sendScheduledEmail":
      case "recordSentEmail":
        actionText = t("sent");
        break;
      case "createActivity":
        switch (type) {
          case "meeting":
            actionText = t("scheduled a meeting");
            break;
          case "call":
            actionText = t("scheduled a call");
            break;
          case "deadline":
            actionText = t("scheduled a deadline");
            break;
          case "task":
            actionText = t("created a task");
            break;
          case "email":
            actionText = t("sent");
            break;
          case "logEmail":
            actionText = t("logged an email");
            break;
          case "lunch":
            actionText = t("created a lunch");
            break;
          case "file":
            actionText = t("uploaded a file");
            break;
        }
        break;
    }
    return actionText;
  };

  const renderNote = () => {
    if (simple) {
      return (
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1 min-w-0 truncate">
            <span className="text-gray-600">{t("created a note")}</span>
            <ActivityHover
              title={summary}
              content={<NoteCard activity={lastActivity} />}
              classNameButton="truncate"
              classNamePanel="max-w-120"
            />
          </div>
          {renderItemInfo()}
        </div>
      );
    }
    return (
      <div className="pb-2">
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1 min-w-0 truncate">
            <span className="text-gray-600">{t("created a note")}</span>
          </div>
          {renderItemInfo()}
        </div>
        <div className="pt-1">
          <NoteCard activity={lastActivity} />
        </div>
      </div>
    );
  };

  const renderEmail = () => {
    const isLogEmail = action === "createActivity" && type === "logEmail";
    if (simple) {
      return (
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1 min-w-0 truncate">
            <span className="text-gray-600">
              {isLogEmail ? t("logged an email") : t("sent an email")}
            </span>
            {!isLogEmail && (
              <ActivityVisibility isEditMode={isMyActivity} activity={lastActivity} />
            )}
            {!isLogEmail && renderLinkEmail()}
            <ActivityHover
              title={summary}
              content={<EmailCard activity={lastActivity} />}
              classNameButton="truncate"
              classNamePanel="max-w-120"
            />
          </div>
          {renderItemInfo()}
        </div>
      );
    }
    return (
      <div className="pb-2">
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1 min-w-0 truncate">
            <span className="text-gray-600">
              {isLogEmail ? t("logged an email") : t("sent an email")}
            </span>
            {!isLogEmail && (
              <ActivityVisibility isEditMode={isMyActivity} activity={lastActivity} />
            )}
            {!isLogEmail && renderLinkEmail()}
          </div>
          {renderItemInfo()}
        </div>
        <div className="pt-1">
          <EmailCard activity={lastActivity} />
        </div>
      </div>
    );
  };

  const renderEmailPrivate = () => {
    const isLogEmail = action === "createActivity" && type === "logEmail";
    return (
      <div className={`flex items-center gap-1 min-w-0 ${className}`}>
        {actor}
        <div className="flex items-center gap-1 min-w-0 truncate">
          <span className="text-gray-600">
            {isLogEmail ? t("logged an email") : t("sent an email")}
          </span>
          {!isLogEmail && <ActivityVisibility isEditMode={isMyActivity} activity={lastActivity} />}
          {!isLogEmail && renderLinkEmail()}
        </div>
        {renderItemInfo()}
      </div>
    );
  };

  const renderSchedule = () => {
    if (simple) {
      return (
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1 min-w-0 truncate">
            <span className="text-gray-600">
              {type === "meeting"
                ? t("scheduled a meeting")
                : type === "call"
                  ? t("scheduled a call")
                  : type === "deadline"
                    ? t("scheduled a deadline")
                    : t("scheduled activity")}
            </span>
            <ActivityHover
              title={summary}
              content={
                <ScheduleCard
                  item={item}
                  activity={lastActivity}
                  onClickItem={() => {
                    const appUrl = `/workspaces/${itemData.projectId}/items/${itemData.id}?type=${itemData.type}`;
                    const jiraUrl = getJirItemUrl({
                      workspaceId: itemData.projectId,
                      itemId: itemData.id,
                      type: itemData.type,
                    });
                    navigateToUrl?.(appUrl, jiraUrl);
                  }}
                />
              }
              classNameButton="truncate"
              classNamePanel="max-w-120"
            />
          </div>
          {renderItemInfo()}
        </div>
      );
    }
    return (
      <div className="pb-2">
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1 min-w-0 truncate">
            <span className="text-gray-600">
              {type === "meeting"
                ? t("scheduled a meeting")
                : type === "call"
                  ? t("scheduled a call")
                  : type === "deadline"
                    ? t("scheduled a deadline")
                    : t("scheduled activity")}
            </span>
          </div>
          {renderItemInfo()}
        </div>
        <div className="pt-1">
          <ScheduleCard
            item={item}
            activity={lastActivity}
            onClickItem={() => {
              const appUrl = `/workspaces/${itemData.projectId}/items/${itemData.id}?type=${itemData.type}`;
              const jiraUrl = getJirItemUrl({
                workspaceId: itemData.projectId,
                itemId: itemData.id,
                type: itemData.type,
              });
              navigateToUrl?.(appUrl, jiraUrl);
            }}
          />
        </div>
      </div>
    );
  };

  if (action === "created") {
    if (isLoadingItem) {
      return (
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          <div className="flex items-center gap-1 min-w-0 flex-wrap">
            <span className="font-medium text-blue-600">...</span>
            <span className="text-gray-600">{t("was created by")}</span>
          </div>
          {actor}
        </div>
      );
    }
    let name;
    if (itemType === "SALE") {
      name = t("Deal");
    } else if (itemType === "CONTACT") {
      name = t("Contact");
    } else if (itemType === "COMPANY") {
      name = t("Account");
    } else if (itemType === "PRODUCT") {
      name = t("Product");
    }
    if (itemData && itemData.id) {
      return (
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          <div className="flex items-center gap-1 min-w-0 flex-wrap">
            <span
              className="font-medium text-blue-600 cursor-pointer hover:underline"
              onClick={() => {
                const appUrl = `/workspaces/${itemData.projectId}/items/${itemData.id}?type=${itemData.type}`;
                const jiraUrl = getJirItemUrl({
                  workspaceId: itemData.projectId,
                  itemId: itemData.id,
                  type: itemData.type,
                });
                navigateToUrl?.(appUrl, jiraUrl);
              }}
            >
              {itemData.data?.name?.value || name}
            </span>
            <span className="text-gray-600">{t("was created by")}</span>
          </div>
          {actor}
        </div>
      );
    }
    return (
      <div className={`flex items-center gap-1 min-w-0 ${className}`}>
        <div className="flex items-center gap-1 min-w-0 flex-wrap">
          <span className="font-medium text-blue-600">{name}</span>
          <span className="text-gray-600">{t("was created by")}</span>
        </div>
        {actor}
      </div>
    );
  }
  if (action === "edited") {
    if (activities.size === 1) {
      const fieldId = lastActivity.getIn(["event", "fieldId"]);
      const fieldValue = lastActivity.getIn(["event", "fieldValue"]);
      const field =
        fields.find((field) => field.get("id") === fieldId) ||
        fromJS({ id: fieldId, label: fieldId });
      return (
        <div className={`flex items-center gap-1 min-w-0 ${className}`}>
          {actor}
          <div className="flex items-center gap-1">
            <span className="text-gray-600">{t("changed")}</span>
            {getFieldIcon(field.toJS(), "w-4 h-4 text-gray-600")}
            <ActivityHover
              title={field.get("label")}
              content={
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 flex items-center gap-1">
                      {getFieldIcon(field.toJS(), "w-4 h-4 text-gray-600")}
                      <span className="text-sm text-gray-600">{field.get("label")}</span>
                    </div>
                    <MoveRight className="w-4 h-4 text-gray-600" />
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-600">{t("Changed to")}</span>
                      <span className="text-sm font-medium text-blue-600">{fieldValue}</span>
                    </div>
                  </div>
                </div>
              }
              classNameButton="whitespace-nowrap"
              classNamePanel="w-max p-2 bg-white rounded-md border border-gray-300"
            />
          </div>
          {renderItemInfo()}
        </div>
      );
    }
    return (
      <div className={`flex items-center gap-1 min-w-0 ${className}`}>
        {actor}
        <div className="flex items-center gap-1">
          <span className="text-gray-600">{t("changed")}</span>
          <ActivityHover
            title={`${activities.size} ${t("attributes")}`}
            content={
              <div className="space-y-2">
                {activities.map((activity) => {
                  const fieldId = activity.getIn(["event", "fieldId"]);
                  const fieldValue = activity.getIn(["event", "fieldValue"]);
                  const field =
                    fields.find((field) => field.get("id") === fieldId) ||
                    fromJS({ id: fieldId, label: fieldId });
                  return (
                    <div key={activity.get("id")} className="flex items-center gap-4">
                      <div className="flex-1 flex items-center gap-1">
                        {getFieldIcon(field.toJS(), "w-4 h-4 text-gray-600")}
                        <span className="text-sm text-gray-600">{field.get("label")}</span>
                      </div>
                      <MoveRight className="w-4 h-4 text-gray-600" />
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-600">{t("Changed to")}</span>
                        <span className="text-sm font-medium text-blue-600">{fieldValue}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            }
            classNameButton="whitespace-nowrap"
            classNamePanel="w-max p-2 bg-white rounded-md border border-gray-300"
          />
        </div>
        {renderItemInfo()}
      </div>
    );
  }
  if (action === "uploadFile" || (action === "createActivity" && type === "file")) {
    const fileName = lastActivity.getIn(["event", "data", "fileName"]);
    return (
      <div className={`flex items-center gap-1 min-w-0 ${className}`}>
        {actor}
        <div className="flex items-center gap-1 min-w-0 truncate">
          <span className="text-gray-600">{t("uploaded a file")}</span>
          <ActivityVisibility isEditMode={isMyActivity} activity={lastActivity} />
          {isShowDetailActivity && (
            <span
              title={fileName}
              className="font-medium text-blue-600 truncate cursor-pointer hover:underline"
              onClick={() => {
                downloadFileLogActivity(fileName).then(openLink);
              }}
            >
              {fileName}
            </span>
          )}
        </div>
        {renderItemInfo()}
      </div>
    );
  }
  if (action === "commented") return renderNote();
  if (
    action === "sendEmail" ||
    action === "sendDraftEmail" ||
    action === "sendScheduledEmail" ||
    action === "recordSentEmail" ||
    (action === "createActivity" && type === "logEmail")
  ) {
    if (isShowDetailActivity) return renderEmail();
    return renderEmailPrivate();
  }
  if (
    action === "createActivity" &&
    (type === "meeting" || type === "call" || type === "deadline")
  ) {
    return renderSchedule();
  }
  if (action === "sent") {
    return (
      <div className={`flex items-center gap-1 min-w-0 ${className}`}>
        {actor}
        <div className="flex items-center gap-1 min-w-0 truncate">
          <span className="text-gray-600">{getActionText()}</span>
        </div>
        {renderItemInfo()}
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-1 min-w-0 ${className}`}>
      {actor}
      <div className="flex items-center gap-1 min-w-0 truncate">
        <span className="text-gray-600">{getActionText()}</span>
        <span title={summary} className="font-medium text-blue-600 truncate">
          {summary}
        </span>
      </div>
      {renderItemInfo()}
    </div>
  );
};

const ActivityViewRow = ({ item, activities }: { item: any; activities: any }) => {
  const navigate = useNavigate();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));

  if (activities.size === 0) return null;

  const lastActivity = activities.first();
  const timeLabel = formatDistanceToNowActivity(lastActivity);

  return (
    <div className="flex items-start px-2">
      <div className="w-40 h-full shrink-0 flex items-start justify-between">
        <div className="pt-1 text-sm text-gray-600">{timeLabel}</div>
        <div className="h-full flex flex-col items-center">
          <div className="border border-gray-200 rounded-full flex items-center justify-center w-7 h-7 shrink-0">
            <ActivityIcon activity={lastActivity} />
          </div>
          <div className="bg-gray-200 h-full min-h-4 w-px" />
        </div>
      </div>
      <div className="pl-2 pt-1 min-w-0">
        <ActivityViewRowContent
          actor={<ActivityActor activity={lastActivity} hideName={true} />}
          item={item}
          activities={activities}
          currentUser={currentUser}
          fields={fields}
          navigateToUrl={(appUrl, jiraUrl) => {
            navigateTo({
              jiraUrlParams: {
                relativeUrl: jiraUrl,
              },
              appUrl: appUrl,
              navigate,
            });
          }}
        />
      </div>
    </div>
  );
};

interface ActivityViewProps {
  item: any;
  activities: any;
}

const ActivityView = ({ item, activities }: ActivityViewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupByTime(activities), [activities]);

  const handleGroupToggle = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const renderGroup = (label: string, list: any[]) => {
    const isCollapsed = collapsedGroups.has(label);
    const listGroup = groupSameDay(fromJS(list));
    return (
      <div key={label} className="flex flex-col gap-2">
        <GroupedSectionHeader
          label={label}
          count={listGroup.size}
          isCollapsed={isCollapsed}
          onToggle={() => handleGroupToggle(label)}
          toggleAriaLabel={isCollapsed ? t("Expand") : t("Collapse")}
        />
        {!isCollapsed &&
          listGroup
            .entrySeq()
            .map(([key, value]) => <ActivityViewRow key={key} item={item} activities={value} />)}
      </div>
    );
  };

  const handleAddActivity = (type: "meeting" | "call" | "deadline") => {
    if (!item.get("id")) return;
    let activityType = ActivityTypes.meeting;
    let summary = "Meeting";
    if (type === "call") {
      activityType = ActivityTypes.call;
      summary = "Call";
    } else if (type === "deadline") {
      activityType = ActivityTypes.deadline;
      summary = "Deadline";
    }
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        activityType,
        summary,
      })
    );
  };

  return (
    <div className="w-full flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between pb-2">
        <h2 className="text-lg font-semibold text-gray-900">{t("Activity")}</h2>
        <Menu as="div" className="relative inline-block text-left">
          <MenuButton className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-md shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)] px-1.5 py-1.5 hover:bg-gray-50">
            <Plus className="w-3 h-3 text-gray-900" />
            <span className="text-xs font-medium text-gray-900 px-1">Add Activity</span>
          </MenuButton>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 z-20 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none p-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => handleAddActivity("meeting")}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm ${
                      focus ? "bg-gray-100" : ""
                    }`}
                  >
                    <CalendarDays className="w-4 h-4 text-gray-700" />
                    <span className="text-gray-900">Meeting</span>
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => handleAddActivity("call")}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm ${
                      focus ? "bg-gray-100" : ""
                    }`}
                  >
                    <Phone className="w-4 h-4 text-gray-700" />
                    <span className="text-gray-900">Call</span>
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => handleAddActivity("deadline")}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm ${
                      focus ? "bg-gray-100" : ""
                    }`}
                  >
                    <Flag className="w-4 h-4 text-gray-700" />
                    <span className="text-gray-900">Deadline</span>
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Transition>
        </Menu>
      </div>

      {/* Timeline */}
      <div className="flex flex-col gap-5 pb-0">
        {/* Today */}
        {groups.today.length > 0 && renderGroup(t("Today"), groups.today)}
        {/* This week */}
        {groups.thisWeek.length > 0 && renderGroup(t("This week"), groups.thisWeek)}
        {/* Months */}
        {Object.entries(groups.months).map(([label, list]) => renderGroup(label, list))}
      </div>

      {/* Empty */}
      {activities.length === 0 && (
        <div className="py-8 text-center text-sm text-gray-600">No activities yet</div>
      )}
    </div>
  );
};

export default ActivityView;
